import { getTelemetrySession, type TelemetrySession } from "./session"

/** Honeycomb UI coordinates, independent of any dataset. */
export interface HoneycombUiEnvironment {
  /** Region-aware, e.g. https://ui.eu1.honeycomb.io. */
  uiBaseUrl: string
  /** Honeycomb team slug — the `/<team>/` path segment. */
  team: string
  /** Honeycomb environment slug (e.g. "uat", "production"). */
  environment: string
}

/** Everything needed to build a deep-link. */
export interface HoneycombUiConfig extends HoneycombUiEnvironment {
  /** `service.name`. Required: the query permalink has no dataset-less form. */
  dataset: string
}

// Org constants: tastytrade's Honeycomb lives on the EU instance under `iggroup`.
const HONEYCOMB_UI_BASE_URL = "https://ui.eu1.honeycomb.io"
const HONEYCOMB_TEAM = "iggroup"

// The deep-link spans the session, capped at 4h so a tab left open all day still
// lands on a readable window, plus 5m past the error for spans arriving late.
const SESSION_WINDOW_MAX_SECONDS = 4 * 60 * 60
const SESSION_WINDOW_TAIL_SECONDS = 5 * 60

/**
 * These are Honeycomb's own environment names, NOT the `deployment.environment`
 * vocabulary, which uses IG's `live` for prod. Don't sync the two to "fix" the
 * divergence — `/environments/live/` is a dead link.
 */
export function honeycombUiForMode(mode: string): HoneycombUiEnvironment {
  return {
    uiBaseUrl: HONEYCOMB_UI_BASE_URL,
    team: HONEYCOMB_TEAM,
    environment: mode === "production" ? "production" : "uat",
  }
}

let honeycombUiConfig: HoneycombUiConfig | undefined

/** Stash the deep-link config at telemetry init so the stamper can read it. */
export function setHoneycombUiConfig(
  config: HoneycombUiConfig | undefined
): void {
  honeycombUiConfig = config
}

/** Permalink to everything this session did around the error. */
export function buildHoneycombSessionUrl(
  session: TelemetrySession,
  errorAt: number
): string | undefined {
  if (!honeycombUiConfig) {
    return undefined
  }
  const { uiBaseUrl, team, environment, dataset } = honeycombUiConfig
  // Both bounds must be whole seconds. Honeycomb rejects the whole permalink
  // otherwise ("incorrect type for field QuerySpec.end_time"), and Sentry's
  // event.timestamp — the usual `errorAt` — carries a fractional part.
  const errorAtSeconds = Math.floor(errorAt)
  const query = {
    calculations: [{ op: "COUNT" }],
    filters: [{ column: "session.id", op: "=", value: session.id }],
    start_time: Math.floor(
      Math.max(session.startedAt, errorAtSeconds - SESSION_WINDOW_MAX_SECONDS)
    ),
    end_time: errorAtSeconds + SESSION_WINDOW_TAIL_SECONDS,
  }
  const encoded = encodeURIComponent(JSON.stringify(query))
  return `${uiBaseUrl}/${team}/environments/${environment}/datasets/${dataset}/?query=${encoded}`
}

/** Structural, so this package needs no Sentry dependency. */
interface SentryEventLike {
  tags?: Record<string, unknown>
  contexts?: Record<string, unknown>
  /** Seconds since the epoch, per Sentry's event payload. */
  timestamp?: number
}

/**
 * Stamp a Sentry error with the Honeycomb session id + deep-link. Correlates on
 * the session, not the trace, because no span is ever active by the time an error
 * surfaces here — see the README for why. No-op until `initTelemetry` sets the UI
 * config, since there'd be no Honeycomb data to point at.
 */
export function applyHoneycombSessionToSentryEvent<T extends SentryEventLike>(
  event: T
): T {
  if (!honeycombUiConfig) {
    return event
  }
  try {
    const session = getTelemetrySession()
    const errorAt = event.timestamp ?? Math.floor(Date.now() / 1000)
    event.tags = { ...event.tags, honeycomb_session_id: session.id }
    const sessionUrl = buildHoneycombSessionUrl(session, errorAt)
    if (sessionUrl) {
      event.contexts = {
        ...event.contexts,
        honeycomb: { session_url: sessionUrl },
      }
    }
  } catch {
    // Correlation is a convenience; never cost the error report itself.
  }
  return event
}
